import { css, html, nothing, TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import store, { waitForUser } from '../../../store/store';
import { loading } from '../../../common/constants/api-constants';
import { ActionStatus } from '../../../common/constants/action-status';
import emit from '../../../common/events';
import { getImageSrcUrl } from '../../../common/utils/product-util';
import { StoreState } from '../../../store/store.interface';
import { loadCustomer } from '../../customer/customer';
import { UserProfile } from '../../user-profile/user-profile.interface';
import { getAnalyticsSafeData, selectUser } from '../../user-profile/user-profile.selectors';
import { fetchUserProfile } from '../../user-profile/user-profile.thunks';
import { getAppFlow } from '../../user-profile/utils/user-profile.util';
import { BaseComponent } from '../../base-component/base-component';
import { getProductTypeRedirectLink, PRODUCT_TYPE } from '../my-account-access-link';
import { checkForRedirectToLogin } from '../../../common/utils/login-utils';
import { fetchMyDigitalProductsAggregated } from './thunks/fetch-my-digital-products-aggregated.thunk';
import {
  getMyDigitalProductsAggregatedData,
  getMyDigitalProductsAggregatedStatus,
} from './store/my-digital-products-aggregated.selectors';
import {
  MyDigitalProductAggregatedItem,
  MyDigitalProductCta,
  MyDigitalProductExpiry,
} from './interface/my-digital-products-aggregated.interface';
import './inclusive-access-panel';
import './connect-how-to-launch-modal';

const OPTED_OUT_EXPIRY_VALUE = 'Opted Out';

@customElement('mhecomm-my-digital-products-v2')
export class MyDigitalProductsV2 extends connect(store)(BaseComponent) {
  static styles = [
    css`
      .loading-container {
        display: grid;
        grid-template-columns: 2fr 8fr 2fr;
        gap: 10px;
      }

      .product-card {
        border: 1px solid #dee2e6;
        border-radius: 0.25rem;
        overflow: hidden;
      }

      .product-card mhecomm-inclusive-access-panel {
        display: block;
        border-top: 1px solid #dee2e6;
      }
    `,
  ];

  @state()
  private user: UserProfile | undefined;

  @state()
  private loading = true;

  @state()
  private loadFailed = false;

  @state()
  private aggregatedProducts: MyDigitalProductAggregatedItem[] = [];

  async connectedCallback() {
    super.connectedCallback();
    store.dispatch(fetchUserProfile());
    store.dispatch(loadCustomer());
    waitForUser().then(() => {
      store.dispatch(fetchMyDigitalProductsAggregated());
    });
  }

  stateChanged(state: StoreState) {
    checkForRedirectToLogin(state.userProfileState.userProfileResponse);
    this.user = selectUser(state);
    this.aggregatedProducts = getMyDigitalProductsAggregatedData(state);
    const status = getMyDigitalProductsAggregatedStatus(state);
    this.loading = status === ActionStatus.idle || status === ActionStatus.loading;
    this.loadFailed = status === ActionStatus.failed;
  }

  renderComp() {
    if (this._isLoading()) {
      return this._renderLoadingTemplate();
    }
    if (this.loadFailed) {
      return html`<p>We couldn't load your digital products. Please try again later.</p>`;
    }
    return this.renderContent();
  }

  renderContent() {
    let eBookshelfLink = null;
    if (this.user) {
      eBookshelfLink = getProductTypeRedirectLink(this.user, PRODUCT_TYPE.E_BOOK);
    }

    return this.aggregatedProducts.length === 0
      ? html`<p class="fs-3">You haven't placed any orders yet with this account</p>`
      : html`
          <div class="row justify-content-sm-center">
            <div class="col-12">
              ${eBookshelfLink
          ? html`<a
                    href="${this.user ? getProductTypeRedirectLink(this.user, PRODUCT_TYPE.E_BOOK) : ''}"
                    class="btn btn-outline-primary w-100 w-md-auto"
                    @click=${(event: Event) => this.gotoMyEbookshelf(event)}
                    aria-label="Go to My eBookshelf? Opens in a new window"
                    target="_blank">
                    Go to
                    <b>My eBookshelf ${this.uxtIconReady ? html`<uxt-icon icon="arrow-right"></uxt-icon>` : ''}</b>
                  </a>`
          : nothing}
              ${this._renderProducts(this.aggregatedProducts)}
            </div>
          </div>
        `;
  }

  /**
   * Renders the consolidated HECLR response in the order supplied by the backend.
   */
  private _renderProducts(items: MyDigitalProductAggregatedItem[]): TemplateResult {
    return html`
      <ul class="list-group">
        ${items.map((product) => {
      const isbn10 = product.identity?.isbn10 ?? '';
      const isbn13 = product.identity?.isbn13;
      const hasIsbn10 = product.identity?.isbn10 !== null;
      const hasProductIdentifiers = hasIsbn10 || Boolean(isbn13);
      const title = product.title?.text || product.inclusiveAccess?.productDescription || isbn10;
      const imageSrc =
        product.image?.url ||
        getImageSrcUrl(product.inclusiveAccess?.coverImage || product.inclusiveAccess?.productIsbn || isbn10);
      const altText = product.image?.altText || title;
      const courseContext = product.courseContext;
      const copyrightYear =
        product.title?.copyrightYear ?? product.identity?.copyrightYear ?? product.inclusiveAccess?.copyrightYear;
      const expiry = product.expiry;
      const ctas = Array.isArray(product.cta) ? product.cta : product.cta ? [product.cta] : [];
      const hasCta = ctas.some((cta) => cta.kind !== 'none' && cta.type !== 'none');
      return html`
            <li class="product-card d-block mt-4">
              <div class="row p-3">
                <div class="col-4 col-sm-3 col-md-2">
                  <img alt=${altText} class="img-fluid" src=${imageSrc} />
                </div>
                <div class=${hasCta ? 'col-8 col-sm-9 col-md-7' : 'col-8 col-sm-9 col-md-10'}>
                  <h2 class="h4 fs-large fw-semibold">
                    <span data-automation-id="item-title-ia-${isbn10}">${title}</span>
                  </h2>
                  ${courseContext?.schoolName
                  ? html`<div><span class="fw-bold">School:</span> ${this._toTitleCase(courseContext.schoolName)}</div>`
          : ''}
                    ${courseContext?.department
                  ? html`<div><span class="fw-bold">Department:</span> ${this._toTitleCase(courseContext.department)}</div>`
          : ''}
                  ${courseContext?.instructorName
                  ? html`<div><span class="fw-bold">Instructor:</span> ${this._toTitleCase(courseContext.instructorName)}</div>`
          : ''}
                    ${courseContext?.courseName
                  ? html`<div><span class="fw-bold">Course:</span> ${this._toTitleCase(courseContext.courseName)}</div>`
          : ''}
                    ${hasProductIdentifiers
                  ? html`<div>
                        ${hasIsbn10 ? html`<span class="fw-bold">ISBN10:</span> ${isbn10}` : nothing}
                        ${hasIsbn10 && isbn13 ? html` | ` : nothing}
                        ${isbn13 ? html`<span class="fw-bold">ISBN13:</span> ${isbn13}` : nothing}
                      </div>`
                  : nothing}
                    ${copyrightYear
                  ? html`<div><span class="fw-bold">Copyright:</span> ©${copyrightYear}</div>`
                  : ''}
                    ${product.typeLabel
                  ? html`<div data-automation-id="item-type-ia-${isbn10}">
                    <span class="fw-bold">Type:</span> ${this._toTitleCase(product.typeLabel)}
                        </div>`
          : ''}
                  ${expiry
          ? html`<div class="mt-2" data-automation-id="item-expiry-ia-${isbn10}">
                        ${this._renderExpiryBadge(expiry)}
                      </div>`
          : ''}
                </div>
                ${hasCta
                  ? html`<div class="col-12 col-md-3 my-auto text-md-end px-2 px-md-0 px-xl-2">
                      <hr class="hr-primary d-md-none" />
                      ${ctas.map((cta) => this._renderCta(cta, product, isbn10))}
                    </div>`
                  : nothing}
              </div>
              ${product.inclusiveAccess
              ? html`<mhecomm-inclusive-access-panel
                  .isbn10=${isbn10}
                  .analyticsEmitter=${this._emitInclusiveAccessAnalytics}></mhecomm-inclusive-access-panel>`
          : nothing}
            </li>
          `;
    })}
      </ul>
    `;
  }

  private _renderExpiryBadge(expiry: MyDigitalProductExpiry): TemplateResult {
    if (expiry.isExpired && expiry.expiresOn === OPTED_OUT_EXPIRY_VALUE) {
      return html`<span class="badge rounded-pill bg-danger text-wrap">${expiry.expiresOn}</span>`;
    }

    if (expiry.isExpired) {
      return html`<span class="badge rounded-pill bg-danger text-wrap">Expired</span>`;
    }

    return html`<span class="badge rounded-pill text-dark text-wrap bg-info">Expires on ${expiry.expiresOn}</span>`;
  }

  private _toTitleCase(value: string): string {
    const minorWords = new Set(['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'nor', 'of', 'on', 'or', 'the', 'to', 'up', 'yet']);
    const words = value.toLocaleLowerCase().split(/\s+/);

    return words
      .map((word, index) =>
        index > 0 && index < words.length - 1 && minorWords.has(word)
          ? word
          : word.replace(/\b[a-z]/g, (character) => character.toLocaleUpperCase())
      )
      .join(' ');
  }

  private _renderCta(
    cta: MyDigitalProductCta,
    product: MyDigitalProductAggregatedItem,
    isbn10: string
  ): TemplateResult | typeof nothing {
    if (cta.kind === 'none' || cta.type === 'none') {
      return nothing;
    }

    const href = cta.url || cta.href || '';
    let primary: TemplateResult;
    if (cta.kind === 'modal' && cta.modal?.type === 'connect-launch') {
      primary = html`<mhecomm-connect-how-to-launch-modal
        title=${product.title?.text || product.inclusiveAccess?.productDescription || isbn10}
        coverImage=${product.image?.url || getImageSrcUrl(product.inclusiveAccess?.coverImage || product.inclusiveAccess?.productIsbn || isbn10)}
        altText=${product.image?.altText || product.title?.text || product.inclusiveAccess?.productDescription || isbn10}
        isbn10=${isbn10}
        isbn13=${product.identity?.isbn13 || ''}
        orderId=${cta.modal.orderId}
        editionOrRelease=${product.title?.edition || ''}
        ?isEvergreenPDP=${product.title?.isEvergreen}
        .user=${this.user}>
      </mhecomm-connect-how-to-launch-modal>`;
    } else if (cta.type === 'mhp_ebook' && cta.ebookUrls?.length) {
      primary = html`<mhecomm-mhp-ebook-download
        isbn=${isbn10}
        .eBookUrls=${cta.ebookUrls}
        instructionsContainerClass="text-md-start"></mhecomm-mhp-ebook-download>`;
    } else if (href) {
      primary = html`<a
        class="btn btn-outline-primary w-100 w-lg-75"
        href=${href}
        target=${cta.target || '_blank'}
        aria-label=${cta.ariaLabel || cta.label}
        data-automation-id=${cta.automationId || `btn-${cta.type}`}>
        ${cta.label}${cta.icon ? html` <uxt-icon icon=${cta.icon}></uxt-icon>` : nothing}
      </a>`;
    } else {
      primary = html`<div>${cta.label}${cta.accessCode ? html`: ${cta.accessCode}` : nothing}</div>`;
    }

    return html`${primary}
      ${cta.secondaryCta?.href
        ? html`<a
            class="btn btn-outline-primary w-100 w-lg-75 mt-2"
            href=${cta.secondaryCta.href}
            target="_blank">
            ${cta.secondaryCta.label}
          </a>`
        : nothing}`;
  }

  gotoMyEbookshelf(event: Event) {
    const link = event.currentTarget as HTMLAnchorElement;
    emit({
      element: this,
      name: 'click',
      detail: {
        id: 'ebookshelf-button',
        label: 'My eBookshelf',
        comp: 'button',
        user: getAnalyticsSafeData(this.user!),
        appFlow: getAppFlow(),
        href: link.href,
      },
      dataLayer: true,
    });
  }

  private _emitInclusiveAccessAnalytics = (name: string, detail: Record<string, unknown>): void => {
    emit({
      element: this,
      name,
      detail,
    });
  };

  _renderLoadingTemplate() {
    return html`
      <div class="loading-container mb-4">
        ${loading({ width: '100%', height: '12rem' })}
        <div>
          ${loading({ height: '1.25rem', width: '70%' })} ${loading({ height: '1.25rem', width: '80%' })}
          ${loading({ height: '1.25rem', width: '40%' })} ${loading({ height: '1.25rem', width: '20%' })}
        </div>
        <div>
          ${loading({ height: '1.25rem', width: '100%' })} ${loading({ height: '1.25rem', width: '80%' })}
          ${loading({ height: '1.25rem', width: '50%' })}
        </div>
      </div>
    `;
  }

  _isLoading() {
    if (window.Mhecomm_Editor) {
      return false;
    }

    return this.loading;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-my-digital-products-v2': MyDigitalProductsV2;
  }
}
